/**
 * A POWER THAT ENDS IN SOMEONE'S INITIALS MAY BE STRUCK (Arthur, 20 October 2026; seen on dev d923, power 4:
 * "...held in the name of the incapacitated person KM ;"). A judge or clerk who strikes a power by hand often initials
 * it, and OCR keeps the initials while it cannot see the line through the words, so the run reads the power as granted.
 * The review screen flags such a power "possibly struck - check page N" and lists it first.
 *
 * The rule: the quote, with one closing ";", "." or "," and the spaces around it set aside, ends in two or three
 * capital letters, plain ("KM") or dotted ("K.M."), as a word of their own. Not flagged:
 *   - an abbreviation a power can legitimately end in (NOT_INITIALS: statutes, agencies, benefits, company forms and
 *     suffixes), short words written in capitals ("LAW", "ACT", "OF"), and a roman numeral after the word that
 *     numbers something ("Article IV");
 *   - undotted letters at the end of a quote written mostly in capitals: in an order typed in capitals a short last
 *     word is a word, not initials. Dotted initials are flagged there too.
 * It errs toward flagging: a false flag costs a look at one page, a missed one a struck power read as granted.
 */
export const NOT_INITIALS: ReadonlySet<string> = new Set([
  // statutes and courts
  'MHL', 'SCPA', 'CPLR', 'EPTL', 'USC', 'CFR', 'NYCRR',
  // agencies and programs
  'HRA', 'DSS', 'DOH', 'OMH', 'HUD', 'SSA', 'SSI', 'SSD', 'IRS', 'VA', 'OTDA', 'NYC', 'NYS', 'NY', 'US', 'USA', 'APS',
  // money, benefits and accounts
  'IRA', 'EIN', 'SSN', 'ATM', 'CD', 'CDS', 'POA', 'HCP', 'PIN',
  // company forms, professions and suffixes
  'LLC', 'LLP', 'INC', 'PC', 'PLLC', 'MD', 'RN', 'DDS', 'CPA', 'ESQ', 'JR', 'SR',
  // short words that end a sentence written in capitals
  'OF', 'TO', 'OR', 'IN', 'ON', 'BY', 'AS', 'AT', 'AN', 'IS', 'IT', 'BE', 'NO', 'SO', 'AND', 'THE', 'FOR', 'ANY', 'ALL', 'LAW',
  'ACT', 'NOT', 'ARE', 'MAY', 'HIM', 'HER', 'HIS', 'ITS', 'ONE', 'TWO', 'SUM', 'USE', 'PAY', 'TAX', 'OWN', 'NEW',
]);

/**
 * A roman numeral is a numeral only after the word that numbers something ("Article IV", "Part II"); after anything
 * else it may be initials (dev corpus d038: "...the incapacitated person IV ;", a struck power).
 */
const NUMERAL_TAIL = /^(?:I{2,3}|IV|VI{0,3}|IX|XI{0,2})$/;
const NUMBERED = /\b(?:article|part|title|section|chapter|schedule|appendix|exhibit|paragraph|rule|level|class|tier)\s*$/i;

const TAIL = /(?:^|[\s,(])([A-Z](?:\.\s?[A-Z]){1,2}\.?|[A-Z]{2,3})$/;

/** The initials a power's quote ends in, if it ends in initials; null otherwise. */
export function endingInitials(quote: string | null | undefined): string | null {
  if (!quote) return null;
  let q = quote.replace(/\s*[;,:]\s*$/, '').replace(/\s+$/, '');
  let m = TAIL.exec(q);
  // "...person KM." : the full stop closes the power, not the initials
  if (!m && q.endsWith('.')) { q = q.slice(0, -1).replace(/\s+$/, ''); m = /(?:^|[\s,(])([A-Z]{2,3})$/.exec(q); }
  if (!m) return null;
  const raw = m[1]!;
  const dotted = raw.includes('.');
  const letters = raw.replace(/[.\s]/g, '');
  if (!dotted && NOT_INITIALS.has(letters)) return null;
  if (!dotted && NUMERAL_TAIL.test(letters) && NUMBERED.test(q.slice(0, q.length - raw.length))) return null;
  if (!dotted) {
    const before = q.slice(0, q.length - raw.length).replace(/[^A-Za-z]/g, '');
    const upper = before.replace(/[^A-Z]/g, '').length;
    if (before.length >= 10 && upper / before.length >= 0.6) return null;
  }
  return letters;
}
